import { cache } from 'react';
import {
  UNIFORM_API_KEY,
  UNIFORM_API_URL,
  UNIFORM_PROJECT_ID,
} from '@/core/constants/env';
import {
  UNIFORM_COMPOSITION_REVALIDATE_TIME,
  UNIFORM_COMPOSITIONS_CACHE_TAG,
  uniformCompositionCacheTag,
} from '@/core/services/uniform/uniform-composition-cache';
import fetchWithResilience from '@/core/utils/fetch-with-resilience';
import {
  withStaleUniformFallback,
} from '@/core/utils/stale-uniform-cache';
import isUniformConfigured from '@/core/utils/uniform/is-uniform-configured';
import { resolveUniformApiLanguage } from '@/core/utils/uniform/resolve-uniform-api-language';

const UNIFORM_EDGE_API_HOST =
  (UNIFORM_API_URL && UNIFORM_API_URL.replace(/\/$/, '')) ||
  'https://uniform.global';

type CompositionResponse = {
  composition?: unknown;
};

/**
 * Fetches a composition by slug and locale using the Uniform composition API.
 * Returns null when Uniform is not configured or the request fails.
 * Call from server only.
 *
 * @param slug - Composition slug (e.g. '/global/header', 'athome')
 * @param locale - App locale (e.g. 'en-nl'); Uniform Canvas uses the **language** code only (see {@link resolveUniformApiLanguage})
 * @param context - Optional label for error logging (e.g. 'header composition')
 */
async function fetchCompositionBySlug(
  slug: string,
  locale: string,
  context: string,
): Promise<unknown> {
  if (!isUniformConfigured()) {
    return null;
  }

  const cacheKey = `uniform-composition:${slug}:${resolveUniformApiLanguage(locale)}`;

  try {
    return await withStaleUniformFallback(cacheKey, async () => {
      const url = new URL('/api/v1/composition', UNIFORM_EDGE_API_HOST);
      url.searchParams.set('projectId', UNIFORM_PROJECT_ID);
      url.searchParams.set('slug', slug);
      url.searchParams.set('locale', resolveUniformApiLanguage(locale));

      const response = await fetchWithResilience(
        url.toString(),
        {
          headers: {
            'x-api-key': UNIFORM_API_KEY,
          },
          next: {
            revalidate: UNIFORM_COMPOSITION_REVALIDATE_TIME,
            tags: [UNIFORM_COMPOSITIONS_CACHE_TAG, uniformCompositionCacheTag(slug)],
          },
        },
        { label: `uniform-composition-${slug}` },
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = (await response.json()) as CompositionResponse;
      return data.composition ?? null;
    });
  } catch (error) {
    console.warn(
      `Failed to fetch ${context} from Uniform (composition may be unpublished or slug may differ):`,
      error instanceof Error ? error.message : error,
    );
    return null;
  }
}

/**
 * Fetches a composition by slug and locale using the Uniform composition API.
 * Results are cached in the Next.js Data Cache (15 min) and deduped per request.
 * Call from server only.
 */
const getCompositionBySlug = cache(
  async (slug: string, locale: string, context = 'composition') =>
    fetchCompositionBySlug(slug, locale, context),
);

export default getCompositionBySlug;
